import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

// Terms of Service for Matrix Shopping (operating MatrixShopping). Swap
// support@matrixvideo.online / privacy@matrixvideo.online for a real inbox
// once one exists. Reviewed lightly for Stripe subscription compliance
// (auto-renewal disclosure, refund policy, cancellation).

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: `By accessing or using MatrixShopping (operated by Matrix Shopping, "the Service"), you agree to be bound by these Terms of Service. If you don't agree, don't use the Service. We may update these terms at any time; continued use after changes constitutes acceptance.`,
  },
  {
    title: "2. Eligibility",
    body: `You must be at least 13 years old to use MatrixShopping. If you're under 18, you must have permission from a parent or legal guardian. By using the Service, you represent that you meet these requirements.`,
  },
  {
    title: "3. Your Account",
    body: `You're responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately of any unauthorized use. We may suspend or terminate accounts that violate these terms.`,
  },
  {
    title: "4. User Content",
    body: `You retain ownership of videos, photos, comments, and other content you upload ("User Content"). By uploading, you grant MatrixShopping a worldwide, non-exclusive, royalty-free license to host, display, distribute, and modify (e.g. transcode, generate thumbnails) your User Content solely for operating the Service. You represent that you own or have all necessary rights to your User Content.`,
  },
  {
    title: "5. Prohibited Conduct",
    body: `You may not: (a) post content that is illegal, infringing, harassing, hateful, sexually explicit involving minors, or promotes violence; (b) impersonate any person or entity; (c) attempt to gain unauthorized access to the Service; (d) scrape, mirror, or resell content; (e) use the Service to distribute malware or spam. We may remove content and terminate accounts at our discretion.`,
  },
  {
    title: "6. MatrixShopping Pro Subscription",
    body: `MatrixShopping Pro is a paid monthly subscription billed via Stripe by Matrix Shopping at $50.00 USD/month. Your subscription automatically renews each month unless cancelled before the renewal date. You can cancel anytime via the "Manage subscription" link in the /pro page; cancellation takes effect at the end of your current billing period. All fees are non-refundable except where required by law. We may change subscription pricing with 30 days' notice.`,
  },
  {
    title: "7. Payments & Tips",
    body: `Tips and coin purchases are processed by Stripe for Matrix Shopping. All transactions are final and non-refundable unless required by law. MatrixShopping is not responsible for payment processing errors, chargebacks, or disputes between users. Creator payouts are subject to platform fees and identity verification.`,
  },
  {
    title: "8. AI-Generated Content",
    body: `Videos generated via the Sora 2 integration are subject to OpenAI's usage policies. You are responsible for ensuring your prompts comply with those policies. We may reject or remove AI-generated content that violates our policies or applicable law.`,
  },
  {
    title: "9. Intellectual Property",
    body: `The MatrixShopping name, logo, design, and code are our property. You may not use them without written permission. All rights not expressly granted are reserved.`,
  },
  {
    title: "10. DMCA & Copyright",
    body: `We respect intellectual property rights. If you believe your copyrighted work has been infringed on MatrixShopping, send a DMCA notice to support@matrixvideo.online including the required elements under 17 U.S.C. § 512(c)(3).`,
  },
  {
    title: "11. Disclaimers",
    body: `The Service is provided "AS IS" without warranties of any kind, express or implied. We don't guarantee uptime, error-free operation, or that content will meet your expectations.`,
  },
  {
    title: "12. Limitation of Liability",
    body: `To the maximum extent permitted by law, MatrixShopping and its affiliates are not liable for indirect, incidental, special, consequential, or punitive damages. Total liability for any claim will not exceed the greater of $100 USD or the amount you paid us in the 12 months preceding the claim.`,
  },
  {
    title: "13. Termination",
    body: `You may stop using the Service at any time. We may suspend or terminate your account with or without notice for violation of these terms or for any reason. Sections 4, 6, 7, 9, 11, 12, 13, and 14 survive termination.`,
  },
  {
    title: "14. Governing Law & Disputes",
    body: `These Terms are governed by the laws of the United States. Any dispute will be resolved by binding arbitration under the American Arbitration Association's Consumer Arbitration Rules. Class actions and jury trials are waived.`,
  },
  {
    title: "15. Contact",
    body: `Questions about these Terms? Email us at support@matrixvideo.online.`,
  },
];

export default function Terms() {
  const nav = useNavigate();
  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <button
          data-testid="terms-back-btn"
          onClick={() => nav(-1)}
          className="text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> back
        </button>
        <header className="mb-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-2">// legal</p>
          <h1 className="font-display font-black text-4xl md:text-5xl flex items-center gap-3 mb-2">
            <FileText className="h-7 w-7 text-[#39FF14]" strokeWidth={2.2} />
            Terms of Service
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            last.updated · feb 2026
          </p>
        </header>
        <div data-testid="terms-content" className="space-y-6 text-white/70 leading-relaxed text-sm">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-white font-display font-black text-lg mb-2">{s.title}</h2>
              <p>{s.body}</p>
            </section>
          ))}
        </div>
        <footer className="mt-14 pt-6 border-t border-white/10 text-white/30 font-mono text-[10px] uppercase tracking-widest">
          Matrix Shopping · operating MatrixShopping · contact support@matrixvideo.online
        </footer>
      </div>
    </div>
  );
}
